import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import { Ruler3D } from 'features/assetViz/objects/decor/Ruler3D'
import { FeatureDispatch } from 'features/assetViz/objects/features/FeatureDispatch'
import {
  cartesianCircleGeometry,
  sphereGeometry,
} from 'features/assetViz/utils/geometryDefines'
import { extractScale, Vec3 } from 'features/assetViz/utils/helpers'
import React from 'react'
import { Vector3 } from 'three'
import {
  GeckoDome,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoSphericalPlate,
} from 'types'

import { ModelSurface3D } from './ModelSurface3D'
import { Surface3DGroupProps } from './Surface3D'

/**
 * A dome (half-sphere), plotted in polar coordinates
 * @param model               The dome component model
 * @param groupProps          Position, rotation, scale, and other 3D properties
 * @constructor
 */
export const DomeSurface3D: React.FC<
  {
    model: GeckoDome
    plantNorthCompassOrientation?: number
  } & Surface3DGroupProps
> = ({ model, ...groupProps }) => {
  //----------------------------------------------------------------------------
  const { show3D } = useAssetVizInternal()
  const { bounds, scale } = React.useMemo(() => {
    // Spherical plot: phi (longitude) from 0 to 360 degrees, theta (latitude) from 0 to 90 degrees (half-sphere)
    const bounds = model.componentBounds

    return {
      bounds,
      scale: extractScale(groupProps.scale).multiply(
        new Vector3(model.radius.num, model.radius.num, model.radius.num)
      ),
    }
  }, [model, groupProps.scale])

  const { plates } = React.useMemo(() => {
    const plates: Array<GeckoSphericalPlate> =
      model instanceof GeckoDome ? model.platesArray : []

    return {
      plates: plates,
    }
  }, [model])

  // Replace the degree markers with Ruler3D components
  const verticalRulerProps = React.useMemo(() => {
    return {
      start: GeckoMeasurement.CreateFromValue(0, GeckoMeasurementUnit.Degrees),
      end: GeckoMeasurement.CreateFromValue(90, GeckoMeasurementUnit.Degrees),
      includeTicks: true,
      includeStart: true,
      includeEnd: true,
      anchor: 'center' as const,
      getTickPosition: (coord: GeckoMeasurement) => {
        const degree = coord.num
        const radians = ((90 - degree) * Math.PI) / 180
        return [0, 1.1 * Math.cos(radians), 1.1 * Math.sin(radians)] as Vec3
      },
    }
  }, [])

  const horizontalRulerProps = React.useMemo(() => {
    return {
      start: GeckoMeasurement.CreateFromValue(0, GeckoMeasurementUnit.Degrees),
      end: GeckoMeasurement.CreateFromValue(360, GeckoMeasurementUnit.Degrees),
      includeTicks: true,
      includeStart: true,
      includeEnd: true,
      anchor: 'center' as const,
      getTickPosition: (coord: GeckoMeasurement) => {
        const degree = coord.num
        const adjustedDegree = (360 - degree + 90) % 360
        const radians = (adjustedDegree * Math.PI) / 180
        return [Math.sin(radians), -0.05, Math.cos(radians)] as Vec3
      },
    }
  }, [])

  const geometry = React.useMemo(() => {
    if (!show3D) return cartesianCircleGeometry
    return sphereGeometry
  }, [show3D])

  //----------------------------------------------------------------------------

  return (
    <ModelSurface3D
      geometry={geometry}
      bounds={bounds}
      componentModel={model}
      {...groupProps}
      // Apply the true scaling last (override anything in groupProps)
      scale={scale}>
      {/* Vertical Degree Ruler */}
      {show3D && <Ruler3D {...verticalRulerProps} />}

      {/* Horizontal Degree Ruler */}
      {show3D && <Ruler3D {...horizontalRulerProps} />}

      <FeatureDispatch features={plates} />
    </ModelSurface3D>
  )
}
